// Backend
import React from 'react';

// Frontend
import { Grid, Cell } from 'react-foundation';

// Functionality
import { Link, useNavigate } from 'react-router-dom';

// Components
import {
  Header,
  FlashMessage,
  ProgressBar,
} from '../../constants/SharedComponents';
import { useDemoContext } from '../../contexts/DemoContext';

// Styles
import '../../stylesheets/components/forms/Conversations.css';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';

const DemoConversationsNew = () => {
  const navigate = useNavigate();
  const { nextDemoStep, addCompletedConversation } = useDemoContext();

  // Objects - matching production structure
  const [loading, setLoading] = React.useState(false);
  const [flashMessageObject, setFlashMessageObject] = React.useState({});
  const [inputs, setInputs] = React.useState({});
  const [data, setData] = React.useState(null);
  const [userInvitationPending, setUserInvitationPending] = React.useState(false);
  const [userInvitationSent, setUserInvitationSent] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [selected, setSelected] = React.useState({});
  const [zeroElements, setZeroElements] = React.useState(false);
  const [badQuery, setBadQuery] = React.useState(null);
  const basicFormButtons = React.useRef([]);
  
  // Navigation state management
  const [navigationHistory, setNavigationHistory] = React.useState(['initial']);

  // Demo helper function for flash messages
  const Flashable = (message, type, closeButton = false) => ({
    message,
    className: type, // FlashMessage component expects 'className' not 'type'
    closeButton,
  });

  // Demo function to simulate enabling/disabling form buttons
  const disableFormButton = (buttons) => {
    buttons.current.forEach((button) => {
      if (button) button.disabled = true;
    });
  };

  // Demo Functions - simulating API calls
  function createConversation(formInputs) {
    // Simulate API delay
    setTimeout(() => {
      // Re-enable form buttons before navigating
      basicFormButtons.current.forEach((button) => {
        if (button) button.disabled = false;
      });
      setLoading(false);
      nextDemoStep();
      
      // Create a demo conversation ID
      const demoConversationId = 'demo-conversation-' + Date.now();
      
      // Determine if user is manager based on role selection
      const isUserManager = formInputs.role === 'employer';
      
      // Create appropriate success message based on role
      const successMessage = isUserManager 
        ? 'Your conversation has been created. The team member has completed their preparation and you can now start the conversation.'
        : 'Demo: Your conversation has been created! In the real app, the recipient would be notified and you could both start prep.';
      
      // If user selected "Manager" role, add a conversation to the table showing completed prep
      if (isUserManager) {
        const newConversation = {
          id: demoConversationId,
          organiserName: 'Demo User', // The current demo user as manager
          attendeeName: 'Jane Smith', // Fixed team member name for demo consistency
          attendeeEmail: 'jane.smith@company.com', // Matching email for Jane Smith
          isOrganiserManager: true, // Demo user is the manager
          isUserManager: true, // Current user is the manager
          isCanvasData: false,
          status: 0, // Conversation not started
          employeePrepStatus: 2, // Team member prep complete
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isDemo: true
        };
        
        // Add to demo context so it appears in the conversations table
        addCompletedConversation(newConversation);
        
        // Navigate back to conversations index to show the new row
        navigate('/demo/conversations', {
          state: {
            flashMessage: Flashable(
              successMessage,
              'notice',
            ),
          },
        });
      } else {
        // Navigate to the demo conversation page for team member role
        navigate(`/demo/conversations/${demoConversationId}`, {
          state: {
            flashMessage: Flashable(
              successMessage,
              'notice',
            ),
            // Pass role information to the success page
            demoUserRole: formInputs.role,
            isUserManager: isUserManager,
          },
        });
      }
    }, 1000);
  }

  function findUser(formInputs) {
    const email = formInputs.emailAddress?.toLowerCase();
    
    // Demo scenarios based on email input
    if (email === 'jane.smith@example.com' || email === 'existing@example.com') {
      // Simulate finding an existing user
      setData({
        forename: 'Jane',
        surname: 'Smith',
        id: 'demo-user-123',
        email_address: email,
      });
      setUserInvitationPending(false);
      setUserInvitationSent(false);
      setFlashMessageObject({});
      setNavigationHistory(prev => [...prev, 'create']);
    } else if (email === 'myself@example.com') {
      // Simulate trying to invite yourself
      setFlashMessageObject(Flashable('Demo: You cannot invite yourself.', 'alert', false));
      setUserInvitationPending(false);
      setUserInvitationSent(false);
    } else if (email?.includes('invalid') || !email?.includes('@')) {
      // Simulate invalid email
      setFlashMessageObject(Flashable('Demo: User email is not valid for your organisation\'s policy', 'alert', false));
      setUserInvitationPending(false);
      setUserInvitationSent(false);
    } else if (email) {
      // Simulate user not found - show invitation flow
      setUserInvitationPending(true);
      setUserInvitationSent(false);
      setFlashMessageObject({});
      setNavigationHistory(prev => [...prev, 'invite']);
    }
  }

  const handleSearch = (query) => {
    setLoading(true);

    // Demo search results
    const demoUsers = [
      { id: 1, emailAddress: 'jane.smith@example.com', firstname: 'Jane', lastname: 'Smith' },
      { id: 2, emailAddress: 'john.doe@example.com', firstname: 'John', lastname: 'Doe' },
      { id: 3, emailAddress: 'sarah.wilson@example.com', firstname: 'Sarah', lastname: 'Wilson' },
    ];

    setTimeout(() => {
      const filteredUsers = demoUsers.filter(user => 
        user.emailAddress.toLowerCase().includes(query.toLowerCase()) ||
        `${user.firstname} ${user.lastname}`.toLowerCase().includes(query.toLowerCase())
      );

      setOptions(filteredUsers);

      if (filteredUsers.length === 0) {
        setZeroElements(true);
        setBadQuery(query);
      } else {
        setZeroElements(false);
        setBadQuery(null);
      }
      setLoading(false);
    }, 300);
  }

  const handleSelected = (option) => {
    if (option[0] && option[0].emailAddress) {
      setSelected(option[0]);
      submitFindUser(option[0]);
    }
  }

  const handleSetInviteUserPage = () => {
    setInputs({
      email_address: badQuery
    });
    setUserInvitationPending(true);
    setUserInvitationSent(false);
    setNavigationHistory(prev => [...prev, 'invite']);
  }

  function inviteUser(formInputs) {
    // Simulate invitation success
    setFlashMessageObject(Flashable('Demo: Invitation sent successfully! In the real app, an email would be sent to the user.', 'notice', false));
    setUserInvitationPending(false);
    setUserInvitationSent(true);
    setNavigationHistory(prev => [...prev, 'invited']);
  }

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    disableFormButton(basicFormButtons);
    createConversation(inputs);
  };

  const findUserSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    findUser(selected);
    setLoading(false);
  };

  const submitFindUser = (formInput) => {
    setLoading(true);
    findUser(formInput);
    setLoading(false);
  };

  const inviteUserSubmit = React.useCallback((event) => {
    event.preventDefault();
    setLoading(true);
    inviteUser(inputs);
    setLoading(false);
  });

  const handleBackNavigation = () => {
    if (navigationHistory.length <= 1) {
      // If we're at the initial state or only have one item, go back to conversations
      navigate('/demo/conversations');
      return;
    }

    // Remove current state and go to previous
    const newHistory = [...navigationHistory];
    newHistory.pop(); // Remove current state
    const previousState = newHistory[newHistory.length - 1];
    
    setNavigationHistory(newHistory);
    
    // Reset to previous state
    switch (previousState) {
      case 'initial':
        setData(null);
        setUserInvitationPending(false);
        setUserInvitationSent(false);
        setSelected({});
        setOptions([]);
        setZeroElements(false);
        setBadQuery(null);
        break;
      case 'invite':
        setData(null);
        setUserInvitationPending(true);
        setUserInvitationSent(false);
        break;
      case 'invited':
        setUserInvitationSent(true);
        setUserInvitationPending(false);
        setData(null);
        break;
      default:
        navigate('/demo/conversations');
    }
  };

  const filterBy = () => true;

  // Content - matching production structure exactly
  return (
    <>
      {/* Custom styling for green radio buttons */}
      <style>
        {`
          .basic-form__input[type="radio"] {
            appearance: none;
            width: 16px;
            height: 16px;
            border: 2px solid #ccc;
            border-radius: 50%;
            background-color: white;
            position: relative;
            cursor: pointer;
            outline: none !important;
            transition: all 0.2s ease;
            margin-right: 8px;
            vertical-align: middle;
            flex-shrink: 0;
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }
          
          .basic-form__input[type="radio"]:checked {
            border-color: var(--color--brand-primary);;
            background-color: white;
            outline: none !important;
          }
          
          .basic-form__input[type="radio"]:checked::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: var(--color--brand-primary);;
          }
          
          .basic-form__input[type="radio"]:focus {
            outline: none !important;
            box-shadow: 0 0 0 2px rgba(80, 176, 100, 0.3);
          }
          
          .basic-form__input[type="radio"]:focus:not(:focus-visible) {
            box-shadow: none !important;
          }
          
          .basic-form__input[type="radio"]:hover:not(:checked) {
            border-color: var(--color--brand-primary);;
          }
          
          .basic-form__field--inline {
            display: flex !important;
            align-items: center !important;
            gap: 0 !important;
            flex-wrap: nowrap !important;
            margin-bottom: 0 !important;
          }
          
          .basic-form__field--inline .basic-form__label {
            margin: 0 !important;
            white-space: nowrap !important;
            cursor: pointer;
            margin-left: 8px !important;
            line-height: 1 !important;
            display: flex !important;
            align-items: center !important;
          }
          
          /* Container for radio button group */
          .radio-group-container {
            display: flex !important;
            gap: 25px !important;
            align-items: center !important;
            flex-wrap: nowrap !important;
            margin-bottom: 15px !important;
          }
        `}
      </style>
      
      <ProgressBar loading={loading} />

      <Header />

      <div className="grid-container">
        <Grid className="grid-padding-x align-justify">
          <Cell small={12}>
            <FlashMessage object={flashMessageObject} columnWrap={false} />

            {!data && !userInvitationPending && !userInvitationSent && (
              <div className="page-content">
                <div className="page-content__header">
                  <Link
                    to="/demo/conversations"
                    className="page-content__button--medium"
                    id="page-navigation-button--go-back"
                  >
                    Back to Dashboard
                  </Link>
                </div>

                <h1 className="page-content__title">
                  Who would you like to have a conversation with?
                </h1>

                <p>
                  Start by entering an email address to search for a user.
                  If they are registered they will appear in a list to be selected from.
                  If that user is not currently registered and does not appear in the list they
                  can be invited by clicking the 'Invite User' button.
                </p>

                <form className="basic-form" onSubmit={findUserSubmit}>
                  <div className="basic-form__field">
                    <label htmlFor="email_address" className="basic-form__label">
                      Name:
                    </label>

                    <AsyncTypeahead
                      filterBy={filterBy}
                      id="email_search"
                      isLoading={loading}
                      minLength={3}
                      labelKey={option => `${option.emailAddress}`}
                      onSearch={handleSearch}
                      options={options}
                      onChange={handleSelected}
                      placeholder="Search for a team member within your organisation..."
                      renderMenuItemChildren={(option) => (
                        <span>{option.emailAddress} - {option.firstname} {option.lastname}</span>
                      )}
                      emptyLabel="No matches found. Please invite this user using the 'Invite User' button below ↓"
                    />
                  </div>

                  {zeroElements && (
                    <div className="basic-form__actions button-set">
                      <input
                        value="Invite User"
                        className="basic-form__button"
                        id="page-form-button--complete"
                        onClick={handleSetInviteUserPage}
                        style={{ marginTop: 40 }}
                      />
                    </div>
                  )}
                </form>

                <div className="demo-notice">
                  <p>
                    <strong>Demo Tips:</strong>
                    <br />
                    • Try typing "jane" or "john" to see existing users
                    <br />
                    • Try "invalid-email" to see error handling
                    <br />
                    • Try "newuser" to see the invite flow
                    <br />
                    • Try "myself@example.com" to see self-invitation error
                  </p>
                </div>
              </div>
            )}

            {userInvitationPending && !userInvitationSent && (<div className="page-content">
              <div className="page-content__header">
                <button
                  onClick={handleBackNavigation}
                  className="page-content__button--medium page-content__button--back"
                  id="page-navigation-button--go-back"
                >
                  Go back
                </button>
              </div>

              <h1 className="page-content__title">Invite User</h1>

              <p>
                Enter the email of the user you wish to invite.
              </p>

              <input
                className="basic-form__input"
                type="email"
                name="email_address"
                id="email_address"
                value={inputs.email_address || ''}
                onChange={handleChange}
                required={true}
                placeholder="Email address (example@email.com)"
                pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
              />
              <p>
                This user isn't registered on the system, click the button below to invite them
              </p>
              <p>
                Please follow your organisations communication policy when sending an invite.
              </p>

              <p>
                <button
                  onClick={inviteUserSubmit}
                  className="page-content__button--medium"
                  id="page-form-button--submit"
                >
                  Invite user
                </button>
              </p>

              <div className="demo-notice">
                <p>
                  <strong>Demo:</strong> This will simulate sending an invitation email to the user.
                </p>
              </div>
            </div>
            )}

            {userInvitationSent && (
              <div className="page-content">
                <h1 className="page-content__title">Invite Sent</h1>

                <Link
                  to="/demo/conversations"
                  className="page-content__button--medium page-content__button--back"
                  id="page-navigation-button--go-back"
                >
                  Go back
                </Link>

                <div className="demo-notice">
                  <p>
                    <strong>Demo:</strong> In the real app, an email invitation would have been sent to the user.
                  </p>
                </div>
              </div>
            )}

            {data && (
              <div className="page-content">                <div className="page-content__header">
                <button
                  onClick={handleBackNavigation}
                  className="page-content__button--medium page-content__button--back"
                  id="page-navigation-button--go-back"
                >
                  Go back
                </button>
              </div>

                <h1 className="page-content__title">Create Conversation</h1>
                <p>
                  Complete the form below to create a conversation with {data.forename}{' '}
                  {data.surname} ({data.email_address})
                </p>
                <form className="basic-form" onSubmit={handleSubmit}>
                  <Grid className="grid-padding-x align-justify">
                    <Cell small={12} large={6}>
                      <div className="basic-form__field">
                        <label htmlFor="forename" className="basic-form__label">
                          First name:
                        </label>

                        <input
                          className="basic-form__input"
                          type="text"
                          name="forename"
                          id="forename"
                          value={data.forename}
                          onChange={handleChange}
                          required={true}
                          placeholder="First name"
                        />
                      </div>

                      <div className="basic-form__field">
                        <label htmlFor="surname" className="basic-form__label">
                          Last name:
                        </label>

                        <input
                          className="basic-form__input"
                          type="text"
                          name="surname"
                          id="surname"
                          value={data.surname}
                          onChange={handleChange}
                          required={true}
                          placeholder="Last name"
                        />
                      </div>

                      <div className="basic-form__field">
                        <label htmlFor="email_address" className="basic-form__label">
                          Email address:
                        </label>

                        <input
                          className="basic-form__input"
                          type="text"
                          name="email_address"
                          id="email_address"
                          value={data.email_address}
                          onChange={handleChange}
                          required={true}
                          placeholder="Email address (example@email.com)"
                        />
                      </div>

                      <p>My role in the conversation</p>

                      <div className="radio-group-container">
                        <div className="basic-form__field basic-form__field--inline">
                          <input
                            className="basic-form__input"
                            type="radio"
                            name="role"
                            id="employee"
                            value="employee"
                            onChange={handleChange}
                            required={true}
                          />

                          <label htmlFor="employee" className="basic-form__label">
                            Team Member
                          </label>
                        </div>

                        <div className="basic-form__field basic-form__field--inline">
                          <input
                            className="basic-form__input"
                            type="radio"
                            name="role"
                            id="employer"
                            value="employer"
                            onChange={handleChange}
                            required={true}
                          />

                          <label htmlFor="employer" className="basic-form__label">
                            Manager
                          </label>
                        </div>
                      </div>
                    </Cell>

                    <Cell small={12} large={6}>
                      <p>You have requested to hold a conversation with {data.forename}.</p>

                      <p>
                        You'll need to specify whether your holding this conversation in the
                        capacity as a manager or as a team member. E.g. are you {data.forename}'s
                        line manager? Or are they yours?
                      </p>

                      <p>
                        Once you click 'submit' {data.forename} will receive an email with further
                        instructions on how to prepare for the conversation. You will see the
                        conversation on your dashboard page and will be invited to prepare for the
                        meeting as well.
                      </p>

                      <p>
                        Remember, you will still need to schedule the meeting, Forward Together simply
                        logs the request.
                      </p>

                      <div className="demo-notice">
                        <p>
                          <strong>Demo:</strong> This will simulate creating the conversation and notifying the participant.
                        </p>
                      </div>
                    </Cell>
                  </Grid>

                  <input value={data.id} name="attendee_id" type="hidden" />

                  <div className="basic-form__actions">
                    <input
                      value="Create Conversation"
                      type="submit"
                      className="basic-form__button basic-form__button-conversation"
                      id="page-form-button--submit"
                      onChange={handleChange}

                      ref={(ref) => (basicFormButtons.current[1] = ref)}
                    />
                  </div>
                </form>
              </div>
            )}
          </Cell>
        </Grid>
      </div>
    </>
  );
};

export { DemoConversationsNew };
